import { useEffect, useState } from "react";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

/** Fetch a full CMS page by slug.
 *  1. Try exported JSON
 *  2. Fallback to backend API
 */
export function useCmsPage(slug) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let dead = false;

    async function loadPage() {
      setLoading(true);
      setError(null);

      try {
        // Try published JSON
        const jsonRes = await fetch(`/cms/${slug}.json`);

        if (jsonRes.ok) {
          const data = await jsonRes.json();
          if (!dead) setPage(data);
          return;
        }

        throw new Error("JSON not found");
      } catch (jsonErr) {
        console.warn(
          `CMS JSON not available for '${slug}', falling back to backend.`,
          jsonErr
        );

        try {
          // Fallback to backend
          const apiRes = await fetch(`${API_BASE}/api/cms/pages/${slug}`);

          if (!apiRes.ok) {
            throw new Error(`Backend returned ${apiRes.status}`);
          }

          const data = await apiRes.json();

          if (!dead) {
            setPage(data);
          }
        } catch (apiErr) {
          if (!dead) {
            setError(apiErr);
          }
        }
      } finally {
        if (!dead) {
          setLoading(false);
        }
      }
    }

    loadPage();

    return () => {
      dead = true;
    };
  }, [slug]);

  return { page, loading, error };
}

/** Helper: get block data by section key + block key from a page. */
export function pickBlock(page, sectionKey, blockKey) {
  if (!page) return null;

  const section = (page.sections || []).find(
    (s) => s.key === sectionKey
  );
  if (!section) return null;

  const block = (section.blocks || []).find(
    (b) => b.key === blockKey
  );

  return block ? block.data : null;
}